import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import Sidebar from "../../components/sidebar/Sidebar";
import PageBreadcrumbs from "../../components/layout/PageBreadcrumbs";
import BrandsPanel from "../../components/client-registry/BrandsPanel";
import LinkedCompaniesPanel from "../../components/client-registry/LinkedCompaniesPanel";
import { selectSidebarCollapsed } from "../../store/slices/app/appSelector";
import { selectUserDb } from "../../store/slices/session/sessionSelector";
import { setToastMessage } from "../../store/slices/app/appSlice";
import {
  ClientAccountSortBy as AccountSortBy,
  ClientAccountView,
  useCreateClientAccountMutation,
  useGetCountriesQuery,
  useListClientAccountsQuery,
  useUpdateClientAccountMutation,
} from "../../store/apis/clientRegistryApi";
import {
  CLIENT_ENGAGEMENTS,
  ClientEngagement,
} from "../../store/apis/commercialProposalApi";

const BUSINESS_UNIT_LABEL: Record<string, string> = {
  BEAPOLO: "BeApolo",
  HOUSE_GROUP: "House Group",
};

/**
 * Registro comercial de clientes: el listado que vivía en dos hojas de cálculo, una por
 * unidad de negocio.
 *
 * Es la fuente del número que va dentro del número de propuesta (HG26-C174-Asus-Plan3Q),
 * así que lo que se da de alta aquí es lo que después se puede elegir en el briefing
 * comercial.
 *
 * Tres cosas que la pantalla impone a propósito:
 *   - El número NO se escribe. Lo asigna el servidor con un contador, para que dos
 *     personas dando de alta a la vez no se peleen por el 214.
 *   - La unidad de negocio y el tipo (cliente/prospect) no se pueden cambiar después: el
 *     número ya está impreso en propuestas emitidas.
 *   - Dar de baja es desactivar, no borrar. Una cuenta con propuestas emitidas no se
 *     puede hacer desaparecer sin dejar huérfano su número.
 */
const ClientRegistry: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const collapsed = useSelector(selectSidebarCollapsed);
  const userDb = useSelector(selectUserDb);
  // Los endpoints de escritura ya exigen ADMIN o STRATEGY; esto solo evita mostrar
  // botones que darían 403.
  const canEdit = userDb?.role === "ADMIN" || userDb?.role === "STRATEGY";

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState("");
  const [businessUnit, setBusinessUnit] = useState<string>("");
  const [type, setType] = useState<string>("");
  // Tres estados y no un interruptor: "" son las dos, "true" solo activas y "false" solo
  // inactivas. Con el interruptor no habia forma de ver las inactivas, que es justo lo
  // que hace falta ahora que el registro las marca.
  const [activeFilter, setActiveFilter] = useState<string>("true");
  const [sortBy, setSortBy] = useState<AccountSortBy | "">("");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data, isFetching, isError, refetch } = useListClientAccountsQuery({
    page: page + 1,
    pageSize,
    search: search.trim() || undefined,
    businessUnit: (businessUnit || undefined) as
      | "BEAPOLO"
      | "HOUSE_GROUP"
      | undefined,
    type: (type || undefined) as "CLIENTE" | "PROSPECT" | undefined,
    active: activeFilter === "" ? undefined : activeFilter === "true",
    sortBy: sortBy || undefined,
    sortDir: sortBy ? sortDir : undefined,
  });

  // Click en la cabecera: la misma columna cambia de sentido, otra empieza por asc.
  const toggleSort = (column: AccountSortBy) => {
    if (sortBy === column) {
      setSortDir((dir) => (dir === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortDir("asc");
    }
    setPage(0);
  };

  const sortableHead = (column: AccountSortBy, label: string) => (
    <TableSortLabel
      active={sortBy === column}
      direction={sortBy === column ? sortDir : "asc"}
      onClick={() => toggleSort(column)}
    >
      {label}
    </TableSortLabel>
  );

  const { data: countriesResponse } = useGetCountriesQuery();
  const countries = countriesResponse?.data ?? [];

  const [createAccount, { isLoading: isCreating }] =
    useCreateClientAccountMutation();
  const [updateAccount, { isLoading: isUpdating }] =
    useUpdateClientAccountMutation();

  const items = data?.data?.items ?? [];
  const total = data?.data?.total ?? 0;

  const [isNewOpen, setIsNewOpen] = useState(false);
  const [editing, setEditing] = useState<ClientAccountView | null>(null);
  const [formName, setFormName] = useState("");
  const [formSector, setFormSector] = useState("");
  // Tipo de cliente. "" es "sin decir", lo normal en un prospect.
  const [formEngagement, setFormEngagement] = useState("");
  const [formBusinessUnit, setFormBusinessUnit] = useState("HOUSE_GROUP");
  const [formType, setFormType] = useState("CLIENTE");
  const [formActive, setFormActive] = useState(true);

  const toast = (message: string, severity: "success" | "error") =>
    dispatch(setToastMessage({ message, severity }));

  const errorMessage = (error: unknown) => {
    const payload = (error as { data?: { message?: string } })?.data;
    return (
      payload?.message ??
      t("clientRegistry.accounts.saveFailed", "No se pudo guardar la cuenta")
    );
  };

  const openNew = () => {
    setFormName("");
    setFormSector("");
    setFormEngagement("");
    setFormBusinessUnit("HOUSE_GROUP");
    setFormType("CLIENTE");
    setIsNewOpen(true);
  };

  const openEdit = (account: ClientAccountView) => {
    setFormName(account.name);
    setFormSector(account.sector ?? "");
    setFormEngagement(account.engagement ?? "");
    setFormActive(account.active);
    setEditing(account);
  };

  const closeDialog = () => {
    setIsNewOpen(false);
    setEditing(null);
  };

  const handleCreate = async () => {
    try {
      const response = await createAccount({
        businessUnit: formBusinessUnit as "BEAPOLO" | "HOUSE_GROUP",
        type: formType as "CLIENTE" | "PROSPECT",
        name: formName.trim(),
        sector: formSector.trim() || undefined,
        engagement: (formEngagement || null) as ClientEngagement | null,
      }).unwrap();
      // El número lo ha decidido el servidor, así que se le dice al usuario cuál le tocó:
      // es el que tendrá que reconocer en los números de propuesta.
      toast(
        response.message ??
          t("clientRegistry.accounts.created", "Cuenta creada"),
        "success"
      );
      closeDialog();
    } catch (error: unknown) {
      toast(errorMessage(error), "error");
    }
  };

  const handleUpdate = async () => {
    if (!editing) return;
    try {
      await updateAccount({
        id: editing.id,
        body: {
          name: formName.trim(),
          sector: formSector.trim() || null,
          engagement: (formEngagement || null) as ClientEngagement | null,
          active: formActive,
        },
      }).unwrap();
      toast(
        t("clientRegistry.accounts.updated", "Cuenta actualizada"),
        "success"
      );
      closeDialog();
    } catch (error: unknown) {
      toast(errorMessage(error), "error");
    }
  };

  const isDialogOpen = isNewOpen || editing !== null;

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Sidebar />

      <Box
        sx={{
          flexGrow: 1,
          px: { xs: 2, md: 4 },
          py: { xs: 2, md: 3 },
          transition: "margin-left 0.3s ease",
          ...(collapsed
            ? { ml: `calc(${theme.spacing(7)} + 12px)` }
            : { ml: `calc(${theme.spacing(32)} + 12px)` }),
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <PageBreadcrumbs />

        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: `1px solid ${theme.palette.grey[200]}`,
            p: { xs: 2, md: 3 },
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 2,
          }}
        >
          <Box display="flex" flexDirection="column" gap={0.5}>
            <Typography variant="h5">
              {t("clientRegistry.title", "Registro de clientes")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t(
                "clientRegistry.subtitle",
                "El número de cliente que va dentro del número de propuesta. Se asigna solo al dar de alta."
              )}
            </Typography>
          </Box>
          <Stack direction="row" gap={1}>
            <Tooltip title={t("clientRegistry.refresh", "Recargar")}>
              <IconButton onClick={() => refetch()} disabled={isFetching}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            {canEdit && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openNew}
              >
                {t("clientRegistry.accounts.add", "Nueva cuenta")}
              </Button>
            )}
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: `1px solid ${theme.palette.grey[200]}`,
            p: { xs: 2, md: 3 },
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            gap={2}
            alignItems={{ md: "center" }}
          >
            <TextField
              size="small"
              label={t("clientRegistry.filters.search", "Buscar")}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(0);
              }}
              placeholder={t(
                "clientRegistry.filters.searchPlaceholder",
                "Nombre o número"
              )}
              sx={{ minWidth: 240 }}
            />

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>
                {t("clientRegistry.filters.businessUnit", "Unidad")}
              </InputLabel>
              <Select
                value={businessUnit}
                label={t("clientRegistry.filters.businessUnit", "Unidad")}
                onChange={(event) => {
                  setBusinessUnit(event.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">
                  {t("clientRegistry.filters.all", "Todas")}
                </MenuItem>
                <MenuItem value="HOUSE_GROUP">House Group</MenuItem>
                <MenuItem value="BEAPOLO">BeApolo</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>
                {t("clientRegistry.filters.type", "Tipo")}
              </InputLabel>
              <Select
                value={type}
                label={t("clientRegistry.filters.type", "Tipo")}
                onChange={(event) => {
                  setType(event.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">
                  {t("clientRegistry.filters.all", "Todas")}
                </MenuItem>
                <MenuItem value="CLIENTE">
                  {t("clientRegistry.type.cliente", "Cliente")}
                </MenuItem>
                <MenuItem value="PROSPECT">
                  {t("clientRegistry.type.prospect", "Prospect")}
                </MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel id="registry-active-filter">
                {t("clientRegistry.filters.active", "Estado")}
              </InputLabel>
              <Select
                labelId="registry-active-filter"
                value={activeFilter}
                label={t("clientRegistry.filters.active", "Estado")}
                onChange={(event) => {
                  setActiveFilter(event.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="true">
                  {t("clientRegistry.filters.activeOnly", "Activas")}
                </MenuItem>
                <MenuItem value="false">
                  {t("clientRegistry.filters.inactiveOnly", "Inactivas")}
                </MenuItem>
                <MenuItem value="">
                  {t("clientRegistry.filters.activeAll", "Todas")}
                </MenuItem>
              </Select>
            </FormControl>
          </Stack>

          {isError && (
            <Alert severity="error">
              {t(
                "clientRegistry.loadFailed",
                "No se pudo cargar el registro. Prueba a recargar."
              )}
            </Alert>
          )}

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width={48} />
                  <TableCell
                    sortDirection={sortBy === "accountNumber" ? sortDir : false}
                  >
                    {sortableHead(
                      "accountNumber",
                      t("clientRegistry.columns.code", "Código")
                    )}
                  </TableCell>
                  <TableCell
                    sortDirection={sortBy === "name" ? sortDir : false}
                  >
                    {sortableHead(
                      "name",
                      t("clientRegistry.columns.name", "Cliente")
                    )}
                  </TableCell>
                  <TableCell>
                    {t("clientRegistry.columns.businessUnit", "Unidad")}
                  </TableCell>
                  <TableCell>
                    {t("clientRegistry.columns.type", "Tipo")}
                  </TableCell>
                  <TableCell>
                    {t("clientRegistry.columns.engagement", "Tipo de cliente")}
                  </TableCell>
                  <TableCell>
                    {t("clientRegistry.columns.sector", "Sector")}
                  </TableCell>
                  <TableCell align="center">
                    {t("clientRegistry.columns.brands", "Marcas")}
                  </TableCell>
                  <TableCell
                    sortDirection={sortBy === "active" ? sortDir : false}
                  >
                    {sortableHead(
                      "active",
                      t("clientRegistry.columns.active", "Activa")
                    )}
                  </TableCell>
                  {canEdit && <TableCell align="right" />}
                </TableRow>
              </TableHead>
              <TableBody>
                {isFetching && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={canEdit ? 9 : 8} align="center">
                      <CircularProgress size={24} sx={{ my: 3 }} />
                    </TableCell>
                  </TableRow>
                )}

                {!isFetching && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={canEdit ? 9 : 8} align="center">
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ my: 3 }}
                      >
                        {t(
                          "clientRegistry.empty",
                          "No hay cuentas con esos filtros"
                        )}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}

                {items.map((account) => {
                  const isOpen = expanded === account.id;
                  return (
                    <React.Fragment key={account.id}>
                      <TableRow hover>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() =>
                              setExpanded(isOpen ? null : account.id)
                            }
                          >
                            {isOpen ? (
                              <KeyboardArrowDownIcon fontSize="small" />
                            ) : (
                              <KeyboardArrowRightIcon fontSize="small" />
                            )}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: "monospace", fontWeight: 600 }}
                          >
                            {account.accountCode}
                          </Typography>
                        </TableCell>
                        <TableCell>{account.name}</TableCell>
                        <TableCell>
                          {BUSINESS_UNIT_LABEL[account.businessUnit] ??
                            account.businessUnit}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={
                              account.type === "CLIENTE"
                                ? t("clientRegistry.type.cliente", "Cliente")
                                : t("clientRegistry.type.prospect", "Prospect")
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {account.engagement
                            ? t(
                                `commercialProposals.engagement.${account.engagement}`,
                                account.engagement
                              )
                            : "—"}
                        </TableCell>
                        <TableCell>{account.sector ?? "—"}</TableCell>
                        <TableCell align="center">
                          {account.brands.length}
                        </TableCell>
                        <TableCell>
                          {account.active ? (
                            <Chip
                              size="small"
                              color="success"
                              label={t("clientRegistry.yes", "Sí")}
                            />
                          ) : (
                            <Chip
                              size="small"
                              label={t("clientRegistry.no", "No")}
                            />
                          )}
                        </TableCell>
                        {canEdit && (
                          <TableCell align="right">
                            <Tooltip title={t("clientRegistry.edit", "Editar")}>
                              <IconButton
                                size="small"
                                onClick={() => openEdit(account)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                      <TableRow>
                        <TableCell
                          colSpan={canEdit ? 9 : 8}
                          sx={{ py: 0, borderBottom: isOpen ? undefined : 0 }}
                        >
                          <Collapse in={isOpen} unmountOnExit>
                            <BrandsPanel
                              account={account}
                              countries={countries}
                              canEdit={canEdit}
                            />
                            {/* Debajo de las marcas porque el numero de marca es lo
                                que elige a que empresa se enlaza. */}
                            <LinkedCompaniesPanel
                              account={account}
                              canEdit={canEdit}
                            />
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, next) => setPage(next)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(event) => {
              setPageSize(Number(event.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100]}
          />
        </Paper>
      </Box>

      <Dialog open={isDialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          {editing
            ? t("clientRegistry.accounts.editTitle", "Editar cuenta")
            : t("clientRegistry.accounts.newTitle", "Nueva cuenta")}
        </DialogTitle>
        <DialogContent>
          <Stack gap={2} mt={1}>
            {editing && (
              <Alert severity="info">
                {t(
                  "clientRegistry.accounts.codeFixed",
                  "El código {{code}} no se puede cambiar: ya puede estar impreso en propuestas emitidas.",
                  { code: editing.accountCode }
                )}
              </Alert>
            )}

            <TextField
              label={t("clientRegistry.columns.name", "Cliente")}
              value={formName}
              onChange={(event) => setFormName(event.target.value)}
              fullWidth
              autoFocus
            />

            <TextField
              label={t("clientRegistry.columns.sector", "Sector")}
              value={formSector}
              onChange={(event) => setFormSector(event.target.value)}
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel>
                {t("clientRegistry.columns.engagement", "Tipo de cliente")}
              </InputLabel>
              <Select
                value={formEngagement}
                label={t(
                  "clientRegistry.columns.engagement",
                  "Tipo de cliente"
                )}
                onChange={(event) => setFormEngagement(event.target.value)}
              >
                <MenuItem value="">
                  {t("clientRegistry.engagementUnset", "Sin decir")}
                </MenuItem>
                {CLIENT_ENGAGEMENTS.map((engagement) => (
                  <MenuItem key={engagement} value={engagement}>
                    {t(
                      `commercialProposals.engagement.${engagement}`,
                      engagement
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {editing ? (
              <FormControlLabel
                control={
                  <Switch
                    checked={formActive}
                    onChange={(event) => setFormActive(event.target.checked)}
                  />
                }
                label={t("clientRegistry.columns.active", "Activa")}
              />
            ) : (
              <>
                <FormControl fullWidth>
                  <InputLabel>
                    {t("clientRegistry.columns.businessUnit", "Unidad")}
                  </InputLabel>
                  <Select
                    value={formBusinessUnit}
                    label={t("clientRegistry.columns.businessUnit", "Unidad")}
                    onChange={(event) =>
                      setFormBusinessUnit(event.target.value)
                    }
                  >
                    <MenuItem value="HOUSE_GROUP">House Group</MenuItem>
                    <MenuItem value="BEAPOLO">BeApolo</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>
                    {t("clientRegistry.columns.type", "Tipo")}
                  </InputLabel>
                  <Select
                    value={formType}
                    label={t("clientRegistry.columns.type", "Tipo")}
                    onChange={(event) => setFormType(event.target.value)}
                  >
                    <MenuItem value="CLIENTE">
                      {t("clientRegistry.type.cliente", "Cliente")}
                    </MenuItem>
                    <MenuItem value="PROSPECT">
                      {t("clientRegistry.type.prospect", "Prospect")}
                    </MenuItem>
                  </Select>
                </FormControl>

                <Alert severity="info">
                  {t(
                    "clientRegistry.accounts.numberAuto",
                    "El número se asigna al guardar y no se puede elegir. La unidad y el tipo tampoco se pueden cambiar después."
                  )}
                </Alert>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>
            {t("clientRegistry.cancel", "Cancelar")}
          </Button>
          <Button
            variant="contained"
            onClick={editing ? handleUpdate : handleCreate}
            disabled={formName.trim().length === 0 || isCreating || isUpdating}
          >
            {t("clientRegistry.save", "Guardar")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientRegistry;
